import React, { useEffect, useState, useMemo } from 'react';
import httpClient from '../../../services/httpClient';
import { useAuth } from '../../../context/AuthContext';
import { useCustomerSelector } from '../../../context/CustomerSelectorContext';
import {
    ROLE_CONFIG,
    PROTECTED_ROLES,
    NON_MANAGEABLE_ROLES,
    TRANSPORT_COMPANY_ONLY_ROLES,
    CLIENT_ONLY_ROLES,
    ROLE_HIERARCHY,
} from '../../../constants/roles';
import { getRoleIcon } from './RoleIcons';
import {
    RolesSection,
    RolesHeader,
    RolesGrid,
    RoleCard,
    RoleIconWrapper,
    RoleName,
    LoadingState,
    EmptyState,
    ErrorState,
    RemoveButton,
    AddRoleSection,
    AddRoleGrid,
    AddRoleCard,
    AddButton,
    ActionsBar,
    SaveButton,
    CancelButton
} from './styles';

export default function RolesManager({ personId, customerId }) {
    const { user } = useAuth();
    const { isManagingOwnData } = useCustomerSelector();
    const [currentRoles, setCurrentRoles] = useState([]);
    const [managedRoles, setManagedRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingChanges, setPendingChanges] = useState({ toAdd: [], toRemove: [] });
    const [isSaving, setIsSaving] = useState(false);

    // Verifica se o usuário tem permissão para gerenciar roles
    const canManageRoles = useMemo(() => {
        const allowedRoles = [
            'ROLE_TRANSPORT_COMPANY',
            'ROLE_LEGAL_PERSON',
            'ROLE_HR_EMPLOYEE',
            'ROLE_HR_EXTERNAL_EMPLOYEE'
        ];
        return user?.roles?.some(role => allowedRoles.includes(role)) || false;
    }, [user?.roles]);

    // Determina a role de maior autoridade do usuário
    const highestUserRole = useMemo(() => {
        if (!user?.roles) return null;
        
        for (const role of ROLE_HIERARCHY) {
            if (user.roles.includes(role)) {
                return role;
            }
        }
        return null;
    }, [user?.roles]);

    // Carrega as roles atuais do colaborador
    useEffect(() => {
        if (!personId || !canManageRoles) {
            setLoading(false);
            return;
        }

        const fetchCurrentRoles = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const queryParam = customerId ? `?customerId=${customerId}` : '';
                const response = await httpClient.get(`/pessoas/${personId}/permissoes${queryParam}`);
                
                if (response.ok) {
                    const data = await response.json();
                    setCurrentRoles(data.roles || []);
                } else {
                    throw new Error('Erro ao carregar permissões');
                }
            } catch (err) {
                console.error('Erro ao buscar roles:', err);
                setError('Não foi possível carregar as permissões');
            } finally {
                setLoading(false);
            }
        };

        fetchCurrentRoles();
    }, [personId, customerId, canManageRoles]);

    // Carrega as roles gerenciáveis
    useEffect(() => {
        if (!canManageRoles || !highestUserRole) {
            return;
        }

        const fetchManagedRoles = async () => {
            try {
                const response = await httpClient.get('/dados/permissoes-gerenciaveis');
                
                if (response.ok) {
                    const data = await response.json();
                    
                    // Encontra as roles gerenciáveis pela role de maior autoridade
                    const userPermissions = data.find(item => item.role === highestUserRole);
                    
                    if (userPermissions && userPermissions.managedRoles) {
                        setManagedRoles(userPermissions.managedRoles);
                    }
                }
            } catch (err) {
                console.error('Erro ao buscar roles gerenciáveis:', err);
            }
        };

        fetchManagedRoles();
    }, [canManageRoles, highestUserRole]);

    // Calcula as roles disponíveis para adicionar
    const availableRolesToAdd = useMemo(() => {
        const currentRoleNames = currentRoles.map(r => r.name);
        const pendingToAddNames = pendingChanges.toAdd.map(r => r);
        const pendingToRemoveNames = pendingChanges.toRemove.map(r => r.name);
        
        return managedRoles.filter(roleName => {
            // Filtrar roles não gerenciáveis (definidas apenas no cadastro)
            if (NON_MANAGEABLE_ROLES.includes(roleName)) {
                return false;
            }
            
            // Não exibir se já tem a role (e não está marcada para remover)
            const hasRole = currentRoleNames.includes(roleName);
            const isMarkedForRemoval = pendingToRemoveNames.includes(roleName);
            const isAlreadyPendingAdd = pendingToAddNames.includes(roleName);
            
            // Filtrar roles exclusivas da empresa de transporte (só para própria empresa)
            const isTransportCompanyOnlyRole = TRANSPORT_COMPANY_ONLY_ROLES.includes(roleName);
            const canUseTransportCompanyRole = isManagingOwnData || !isTransportCompanyOnlyRole;
            
            // Filtrar roles exclusivas de clientes (só para colaboradores de clientes)
            const isClientOnlyRole = CLIENT_ONLY_ROLES.includes(roleName);
            const canUseClientRole = !isManagingOwnData || !isClientOnlyRole;
            
            return (!hasRole || isMarkedForRemoval) && 
                   !isAlreadyPendingAdd && 
                   canUseTransportCompanyRole && 
                   canUseClientRole;
        });
    }, [managedRoles, currentRoles, pendingChanges, isManagingOwnData]);

    // Mantém referência das roles originais para comparação
    const originalRoleNames = useMemo(() => {
        return currentRoles.map(r => r.name);
    }, [currentRoles]);

    // Calcula as roles atuais (incluindo mudanças pendentes)
    const displayedRoles = useMemo(() => {
        // Roles atuais que não foram marcadas para remover
        const notMarkedForRemoval = currentRoles.filter(role => 
            !pendingChanges.toRemove.some(r => r.name === role.name)
        );
        
        // Adiciona as roles pendentes de adição
        // Só marca como "novo" se não estava na lista original
        const toAddAsObjects = pendingChanges.toAdd.map(roleName => ({
            name: roleName,
            isPending: !originalRoleNames.includes(roleName)
        }));
        
        // Ordem: normais + novas (roles marcadas para remoção NÃO aparecem aqui)
        return [...notMarkedForRemoval, ...toAddAsObjects];
    }, [currentRoles, pendingChanges, originalRoleNames]);

    const handleAddRole = (roleName) => {
        // Se a role estava marcada para remoção, remove da lista de remoção
        const wasMarkedForRemoval = pendingChanges.toRemove.some(r => r.name === roleName);
        
        if (wasMarkedForRemoval) {
            setPendingChanges(prev => ({
                ...prev,
                toRemove: prev.toRemove.filter(r => r.name !== roleName)
            }));
        } else {
            // Caso contrário, adiciona à lista de adição
            setPendingChanges(prev => ({
                ...prev,
                toAdd: [...prev.toAdd, roleName]
            }));
        }
    };

    const handleRemoveRole = (role) => {
        // Se é uma role pendente de adição, apenas remove da lista de adição
        if (role.isPending) {
            setPendingChanges(prev => ({
                ...prev,
                toAdd: prev.toAdd.filter(r => r !== role.name)
            }));
        } else {
            // Se é uma role existente, marca para remoção
            setPendingChanges(prev => ({
                ...prev,
                toRemove: [...prev.toRemove, role]
            }));
        }
    };

    const canRemoveRole = (roleName) => {
        // Não pode remover roles protegidas nem roles não gerenciáveis
        return !PROTECTED_ROLES.includes(roleName) && !NON_MANAGEABLE_ROLES.includes(roleName);
    };

    const hasPendingChanges = useMemo(() => {
        return pendingChanges.toAdd.length > 0 || pendingChanges.toRemove.length > 0;
    }, [pendingChanges]);

    const handleSaveChanges = async () => {
        if (!hasPendingChanges) return;

        setIsSaving(true);
        setError(null);

        try {
            const queryParam = customerId ? `?customerId=${customerId}` : '';

            // Remove roles (se houver)
            if (pendingChanges.toRemove.length > 0) {
                const removePayload = {
                    target: personId,
                    roles: pendingChanges.toRemove.map(r => r.name)
                };

                const removeResponse = await httpClient.request(
                    `/pessoas/${personId}/permissoes${queryParam}`,
                    {
                        method: 'DELETE',
                        body: JSON.stringify(removePayload)
                    }
                );

                if (!removeResponse.ok) {
                    throw new Error('Erro ao remover permissões');
                }
            }

            // Adiciona roles (se houver)
            if (pendingChanges.toAdd.length > 0) {
                const addPayload = {
                    target: personId,
                    roles: pendingChanges.toAdd
                };

                const addResponse = await httpClient.post(
                    `/pessoas/${personId}/permissoes${queryParam}`,
                    addPayload
                );

                if (!addResponse.ok) {
                    throw new Error('Erro ao adicionar permissões');
                }
            }

            // Limpa mudanças pendentes
            setPendingChanges({ toAdd: [], toRemove: [] });

            // Recarrega as roles atualizadas
            const response = await httpClient.get(`/pessoas/${personId}/permissoes${queryParam}`);
            if (response.ok) {
                const data = await response.json();
                setCurrentRoles(data.roles || []);
            }
        } catch (err) {
            setError('Erro ao salvar alterações. Tente novamente.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelChanges = () => {
        setPendingChanges({ toAdd: [], toRemove: [] });
    };

    // Não renderiza se o usuário não tiver permissão
    if (!canManageRoles) {
        return null;
    }

    if (loading) {
        return (
            <RolesSection>
                <RolesHeader>Permissões do Colaborador</RolesHeader>
                <LoadingState>
                    <div className="spinner" />
                    <p>Carregando permissões...</p>
                </LoadingState>
            </RolesSection>
        );
    }

    if (error) {
        return (
            <RolesSection>
                <RolesHeader>Permissões do Colaborador</RolesHeader>
                <ErrorState>{error}</ErrorState>
            </RolesSection>
        );
    }

    return (
        <RolesSection>
            <RolesHeader>
                Permissões do Colaborador
                <span className="count">({displayedRoles.length})</span>
                {hasPendingChanges && (
                    <>
                        <span className="pending-indicator">Não salvo</span>
                        <ActionsBar>
                            <CancelButton 
                                type="button"
                                onClick={handleCancelChanges}
                                disabled={isSaving}
                                title="Descartar alterações"
                            >
                                Descartar
                            </CancelButton>
                            <SaveButton 
                                type="button"
                                onClick={handleSaveChanges}
                                disabled={isSaving}
                                title="Salvar alterações de permissões"
                            >
                                {isSaving ? '...' : '✓ Salvar Permissões'}
                            </SaveButton>
                        </ActionsBar>
                    </>
                )}
            </RolesHeader>
            
            {displayedRoles.length === 0 ? (
                <EmptyState>
                    <div className="icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="12" cy="16" r="1" fill="currentColor"/>
                        </svg>
                    </div>
                    <p>Nenhuma permissão atribuída</p>
                </EmptyState>
            ) : (
                <RolesGrid>
                    {displayedRoles.map((role, index) => {
                        const config = ROLE_CONFIG[role.name] || {
                            color: '#95a5a6',
                            label: role.name
                        };
                        
                        const isRemovable = canRemoveRole(role.name);
                        const isPending = role.isPending;
                        
                        return (
                            <RoleCard 
                                key={`${role.name}-${index}`} 
                                $color={config.color}
                                $isPending={isPending}
                            >
                                {isRemovable && (
                                    <RemoveButton
                                        type="button"
                                        onClick={() => handleRemoveRole(role)}
                                        title="Remover permissão"
                                    >
                                        ×
                                    </RemoveButton>
                                )}
                                <RoleIconWrapper $color={config.color}>
                                    {getRoleIcon(role.name)}
                                </RoleIconWrapper>
                                <RoleName>
                                    {config.label}
                                    {isPending && <span className="pending-badge">Novo</span>}
                                </RoleName>
                            </RoleCard>
                        );
                    })}
                </RolesGrid>
            )}

            {availableRolesToAdd.length > 0 && (
                <AddRoleSection>
                    <RolesHeader>
                        Adicionar Permissões
                    </RolesHeader>
                    <AddRoleGrid>
                        {availableRolesToAdd.map((roleName) => {
                            const config = ROLE_CONFIG[roleName] || {
                                color: '#95a5a6',
                                label: roleName
                            };
                            
                            return (
                                <AddRoleCard key={roleName} $color={config.color}>
                                    <RoleIconWrapper $color={config.color} style={{ width: '28px', height: '28px' }}>
                                        {getRoleIcon(roleName)}
                                    </RoleIconWrapper>
                                    <RoleName style={{ fontSize: '12px' }}>{config.label}</RoleName>
                                    <AddButton
                                        type="button"
                                        onClick={() => handleAddRole(roleName)}
                                        $color={config.color}
                                    >
                                        + Adicionar
                                    </AddButton>
                                </AddRoleCard>
                            );
                        })}
                    </AddRoleGrid>
                </AddRoleSection>
            )}
        </RolesSection>
    );
}
